/**
 * <read-more-text data-lines="10">
 *   <div class="read-more__content is-clamped" data-read-more-content>…</div>
 *   <button data-read-more-toggle hidden>
 *     <span data-read-more-label>Read more</span>
 *   </button>
 * </read-more-text>
 *
 * Clamps content to data-lines lines. Shows the toggle only when the text
 * overflows, then animates open/closed by transitioning max-height between
 * the measured collapsed and full heights.
 */
class ReadMoreText extends HTMLElement {
  constructor() {
    super();
    this.content = this.querySelector('[data-read-more-content]');
    this.toggle = this.querySelector('[data-read-more-toggle]');
    this.label = this.querySelector('[data-read-more-label]');
    this.lines = parseInt(this.dataset.lines, 10) || 10;
    this.expanded = false;
    this.animating = false;

    this.moreText = this.dataset.moreText || 'Read more';
    this.lessText = this.dataset.lessText || 'Read less';
    this.duration = parseInt(this.dataset.duration, 10) || 350;
  }

  connectedCallback() {
    if (!this.content || !this.toggle) return;

    this.content.style.webkitLineClamp = String(this.lines);
    requestAnimationFrame(() => this.evaluate());

    this.toggle.addEventListener('click', this.onToggle.bind(this));

    this._onResize = this.debounce(() => {
      if (!this.expanded && !this.animating) this.evaluate();
    }, 150);
    window.addEventListener('resize', this._onResize);
  }

  disconnectedCallback() {
    if (this._onResize) window.removeEventListener('resize', this._onResize);
  }

  evaluate() {
    this.content.classList.add('is-clamped');
    const overflowing = this.content.scrollHeight > this.content.clientHeight + 1;

    if (overflowing) {
      this.toggle.hidden = false;
    } else {
      this.toggle.hidden = true;
      this.content.classList.remove('is-clamped');
    }
  }

  onToggle() {
    if (this.animating) return;
    this.expanded ? this.collapse() : this.expand();
  }

  expand() {
    this.animating = true;
    this.expanded = true;

    // Current (clamped) height is the starting point.
    const startHeight = this.content.clientHeight;

    // Remove clamp to measure the full height, but pin max-height to
    // the start so there's no visual jump.
    this.content.style.maxHeight = startHeight + 'px';
    this.content.style.overflow = 'hidden';
    this.content.classList.remove('is-clamped');

    const fullHeight = this.content.scrollHeight;

    // Next frame: transition from start to full.
    requestAnimationFrame(() => {
      this.content.style.transition = `max-height ${this.duration}ms ease`;
      this.content.style.maxHeight = fullHeight + 'px';
    });

    this.setLabel(this.lessText);
    this.toggle.setAttribute('aria-expanded', 'true');

    this.afterTransition(() => {
      // Let content flow freely once open (handles later reflows).
      this.content.style.transition = '';
      this.content.style.maxHeight = '';
      this.content.style.overflow = '';
      this.animating = false;
    });
  }

  collapse() {
    this.animating = true;
    this.expanded = false;

    // Pin to current full height, then animate down to the clamped height.
    const fullHeight = this.content.scrollHeight;
    this.content.style.overflow = 'hidden';
    this.content.style.maxHeight = fullHeight + 'px';

    // Measure the clamped target height without flashing: temporarily
    // clamp in a hidden measure pass.
    const targetHeight = this.measureClampedHeight();

    requestAnimationFrame(() => {
      this.content.style.transition = `max-height ${this.duration}ms ease`;
      this.content.style.maxHeight = targetHeight + 'px';
    });

    this.setLabel(this.moreText);
    this.toggle.setAttribute('aria-expanded', 'false');

    this.afterTransition(() => {
      this.content.classList.add('is-clamped');
      this.content.style.transition = '';
      this.content.style.maxHeight = '';
      this.content.style.overflow = '';
      this.animating = false;

      const top = this.getBoundingClientRect().top;
      if (top < 0) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  // Measure what the clamped height would be, without a visible flash.
  measureClampedHeight() {
    const prevTransition = this.content.style.transition;
    const prevMaxHeight = this.content.style.maxHeight;

    this.content.style.transition = 'none';
    this.content.style.maxHeight = 'none';
    this.content.classList.add('is-clamped');
    const h = this.content.clientHeight;
    this.content.classList.remove('is-clamped');

    this.content.style.transition = prevTransition;
    this.content.style.maxHeight = prevMaxHeight;
    return h;
  }

  afterTransition(cb) {
    let done = false;
    const handler = (e) => {
      if (e && e.propertyName && e.propertyName !== 'max-height') return;
      if (done) return;
      done = true;
      this.content.removeEventListener('transitionend', handler);
      cb();
    };
    this.content.addEventListener('transitionend', handler);
    // Fallback in case transitionend doesn't fire.
    setTimeout(() => handler(), this.duration + 50);
  }

  setLabel(text) {
    if (this.label) this.label.textContent = text;
    else this.toggle.textContent = text;
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('read-more-text')) {
  customElements.define('read-more-text', ReadMoreText);
}
